/**
 * Layer 1 of demo security stack — full-screen Confidential Demonstration
 * Notice. Renders ONLY when user.is_demo === true and the viewer has not yet
 * accepted the recording-restriction click-through for this session.
 *
 * Hard-blocks ALL admin pages until the user has:
 *   1. Scrolled to the bottom of the notice
 *   2. Checked both consent boxes
 *   3. Clicked "Accept & Enter Secure Demo"
 *
 * Acceptance is recorded server-side via POST /api/public/demo-acceptance and
 * persisted in localStorage for the duration of the 30-min cookie session so a
 * page refresh does not re-prompt.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Lock, AlertTriangle, ShieldAlert, FileText, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

const RESTRICTIONS = [
  "Record this session in any form",
  "Capture screenshots",
  "Capture video",
  "Capture audio",
  "Share screen content with third parties",
  "Forward demo links",
  "Reproduce platform workflows",
  "Reverse engineer platform architecture",
];

export default function DemoSecurityGate({ onAccepted }) {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  // Hydrate prior acceptance from the server (un-bypassable). LocalStorage is
  // only used to skip the network round-trip on subsequent renders during the
  // SAME accepted session; it is NEVER trusted alone.
  useEffect(() => {
    if (user?.is_demo !== true) return;
    let alive = true;
    axios.get(`${API}/public/demo-session`)
      .then((r) => {
        if (!alive) return;
        if (r.data?.is_demo) {
          setSession(r.data);
          // SERVER is source of truth — only mark accepted if backend says so.
          if (r.data.accepted === true) {
            const key = `dgx_demo_accepted_${r.data.session_id || "x"}`;
            localStorage.setItem(key, "1");
            setAccepted(true);
          } else {
            // Force re-acknowledgement if server hasn't recorded acceptance,
            // even if a stale localStorage flag is present.
            setAccepted(false);
          }
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  if (!user || user.is_demo !== true) return null;
  if (accepted) {
    if (typeof onAccepted === "function") onAccepted(session);
    return null;
  }

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setScrolled(true);
  };

  const canAccept = scrolled && check1 && check2 && !submitting;

  const accept = async () => {
    if (!canAccept) return;
    setSubmitting(true);
    setErr("");
    try {
      await axios.post(`${API}/public/demo-acceptance`, {
        session_id: session?.session_id || "",
        via: session?.via || "",
        accepted_clauses: ["confidential-demonstration-notice", "no-recording", "no-distribution"],
      });
      localStorage.setItem(`dgx_demo_accepted_${session?.session_id || "x"}`, "1");
      setAccepted(true);
      if (typeof onAccepted === "function") onAccepted(session);
    } catch (e) {
      setErr("Could not record acceptance. Please refresh and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fundName = session?.fund_name || "Your Fund";
  const investor = session?.investor_name || "";

  return (
    <div className="fixed inset-0 z-[200] bg-[#050A07]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 overflow-y-auto" data-testid="demo-security-gate">
      <div className="w-full max-w-3xl bg-[#09120D] border border-[#22C55E]/30 shadow-[0_0_60px_rgba(34,197,94,0.15)]">
        {/* Header */}
        <div className="border-b border-[#1A2E22] px-6 sm:px-8 py-5 flex items-center gap-3">
          <ShieldAlert className="w-5 h-5 text-[#22C55E]" />
          <div className="flex-1 min-w-0">
            <p className="text-[#22C55E] text-[10px] uppercase tracking-[0.3em] font-bold">Confidential Demonstration Notice</p>
            <p className="text-white font-['Outfit'] text-lg sm:text-xl font-bold leading-tight mt-0.5">
              For {investor ? `${investor} · ` : ""}{fundName} · Session {session?.session_id || "DGX-XXXXX"}
            </p>
          </div>
        </div>

        {/* Body — scroll lock */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="px-6 sm:px-8 py-6 max-h-[55vh] overflow-y-auto text-[#C5D5CC] text-sm leading-relaxed space-y-4"
          data-testid="demo-security-body"
        >
          <p>
            This demonstration contains proprietary business workflows, trade intelligence
            models, AI decision frameworks, commercial methodologies, interface systems,
            and confidential intellectual property owned exclusively by Delta GreenX.
          </p>
          <p className="text-white font-semibold">By proceeding, you expressly agree that you shall NOT:</p>
          <ul className="space-y-1.5 pl-5 list-disc marker:text-[#22C55E]">
            {RESTRICTIONS.map((r) => <li key={r}>{r}</li>)}
          </ul>
          <p className="border-l-2 border-[#22C55E] pl-4 text-[#A1B2A8] text-xs italic">
            This session is uniquely watermarked and tracked. All accesses are logged with
            timestamp, session ID, browser fingerprint, and viewer attribution. Any
            unauthorized capture, reproduction, distribution, or misuse may result in
            immediate legal action including claims for damages, injunctive relief, and
            intellectual property enforcement under applicable law.
          </p>
          <p>
            By continuing, you acknowledge that you have read, understood, and accept these
            conditions in full. Your acceptance is recorded as a binding click-through
            agreement.
          </p>
          <div className="flex items-start gap-2 text-[#A1B2A8]/70 text-xs italic mt-4 pt-4 border-t border-[#1A2E22]">
            <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <p>
              For deeper diligence under formal investment process, a mutual NDA + non-circumvention +
              non-use clauses will be executed before any sensitive module is unlocked. This
              click-through is the lite first layer.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#1A2E22] bg-[#070F0A] px-6 sm:px-8 py-5 space-y-3">
          {!scrolled && (
            <p className="text-yellow-400/80 text-xs flex items-center gap-1.5" data-testid="demo-security-scroll-hint">
              <AlertTriangle className="w-3.5 h-3.5" /> Please scroll to read the full notice before continuing.
            </p>
          )}

          <label className={`flex items-start gap-2 text-sm cursor-pointer select-none ${scrolled ? "text-white" : "text-[#A1B2A8]/40 cursor-not-allowed"}`}>
            <input
              type="checkbox"
              checked={check1}
              disabled={!scrolled}
              onChange={(e) => setCheck1(e.target.checked)}
              className="mt-0.5 accent-[#22C55E] w-3.5 h-3.5 flex-shrink-0"
              data-testid="demo-security-check1"
            />
            <span>I have read and accept the Confidential Demonstration Notice in full.</span>
          </label>
          <label className={`flex items-start gap-2 text-sm cursor-pointer select-none ${scrolled ? "text-white" : "text-[#A1B2A8]/40 cursor-not-allowed"}`}>
            <input
              type="checkbox"
              checked={check2}
              disabled={!scrolled}
              onChange={(e) => setCheck2(e.target.checked)}
              className="mt-0.5 accent-[#22C55E] w-3.5 h-3.5 flex-shrink-0"
              data-testid="demo-security-check2"
            />
            <span>I will not record, screenshot, capture, share, forward, reproduce, or reverse-engineer any part of this session.</span>
          </label>

          {err && <p className="text-red-400 text-xs" data-testid="demo-security-error">{err}</p>}

          <div className="flex items-center justify-between gap-3 pt-2">
            <a
              href="/legal/investor-disclaimer"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A1B2A8] hover:text-white text-xs flex items-center gap-1"
            >
              <Lock className="w-3 h-3" /> View full Investor Disclaimer
            </a>
            <button
              onClick={accept}
              disabled={!canAccept}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#22C55E] text-[#050A07] font-['Outfit'] font-semibold text-sm hover:bg-[#1CAB4D] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="demo-security-accept-btn"
            >
              {submitting ? "Recording acceptance…" : <>Accept &amp; Enter Secure Demo <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
